import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { MdCheckCircle, MdOutlineRocketLaunch } from 'react-icons/md'
import { useAssistant, useCreateAssistantTemplate, useDeleteAssistant, useReferenceData, useUpdateAssistant } from '~/api'
import { BubblesIcon } from '~/assets/BubblesIcon'
import { Spinner } from '~/assets/Spinner'
import { AppHeading } from '~/components/educator/app/AppHeading'
import { appThemes, templateTags, templateTheme } from '~/model'
import { Button, Checkbox, cn, DeleteButtonWithConfirmationDialog, Label, RadioGroup, RadioGroupButtonItem, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator } from '~ui'

export const Route = createFileRoute('/educator/courses/$courseId/apps/$appId/settings')({
	component: Settings,
})

function Settings() {
	const { appId } = Route.useParams()
	const navigate = useNavigate()
	const app = useAssistant(appId)
	const { mutate: createAssistantTemplate, isPending: isCreatingTemplate, isSuccess: isTemplateCreated } = useCreateAssistantTemplate()
	const { mutate: updateAssistant } = useUpdateAssistant()
	const { mutate: deleteAssistant } = useDeleteAssistant()
	const { data: referenceData } = useReferenceData()

	const [selectedModel, setSelectedModel] = useState<string>('')
	const [iconTheme, setIconTheme] = useState<string>('')
	const [selectedTemplateTags, setSelectedTemplateTags] = useState<string[]>([])

	useEffect(() => {
		if (app) {
			setSelectedModel(app.llmConfiguration.modelId)
			setIconTheme(app.iconTheme ?? '')
		}
	}, [app])

	if (!app) return null

	return (
		<>
			<AppHeading>
				<div className="flex items-center gap-[10px]">
					<Button
						onClick={() => {
							if (!selectedModel || !iconTheme || !selectedModel) throw new Error('Missing required fields')
							updateAssistant({
								...app,
								iconTheme: iconTheme,
								llmConfiguration: {
									temperature: app.llmConfiguration.temperature,
									modelId: selectedModel,
								},
							})
						}}
					>
						Update settings
					</Button>
				</div>
			</AppHeading>

			<h1 className="mb-[4px]">Settings</h1>
			<p className="max-w-text mb-[24px]">Configure the app’s settings to suit your teaching needs and student experience.</p>

			<Separator />

			<Label
				htmlFor="model"
				infoTooltip="Select the language model this app will use to generate responses. Different models may produce different styles and levels of detail."
			>
				Large language model (LLM)
			</Label>

			<Select
				disabled={app.isGlobal === true || app.isTemplate === true}
				value={selectedModel}
				onValueChange={(value) => {
					setSelectedModel(value)
				}}
			>
				<SelectTrigger>
					<SelectValue placeholder="Select a model" />
				</SelectTrigger>
				<SelectContent id="model">
					{referenceData?.aiModels.map((model) => (
						<SelectItem
							key={model.id}
							value={model.id}
						>
							{model.name}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<Separator className="my-[24px]" />

			<Label htmlFor="information-sources">App icon</Label>
			<p className="max-w-text mb-[24px]">Select the icon that you want to display for this app</p>
			<RadioGroup
				value={iconTheme}
				onValueChange={(value) => {
					setIconTheme(value)
				}}
				disabled={app.isTemplate === true}
				className="flex flex-wrap gap-3"
			>
				{app.isTemplate && (
					<RadioGroupButtonItem value={templateTheme}>
						<div className="bg-light-grey-25 size-[32px] items-center justify-center">
							<BubblesIcon className="text-navy-100 size-[32px]" />
						</div>
					</RadioGroupButtonItem>
				)}
				{appThemes.map((theme) => (
					<RadioGroupButtonItem
						key={theme}
						value={theme}
					>
						<div
							className="size-[32px] items-center justify-center bg-white"
							style={{ backgroundColor: theme }}
						>
							<BubblesIcon className={cn(theme === '#000f46' ? 'text-white' : 'text-navy-100', 'size-[32px]')} />
						</div>
					</RadioGroupButtonItem>
				))}
			</RadioGroup>

			<Separator className="my-[24px]" />

			{!app.isGlobal && !app.isTemplate && (
				<>
					<div className="mb-[24px] flex size-[40px] shrink-0 items-center justify-center bg-pink-100">
						<MdOutlineRocketLaunch
							className="size-[24px] text-pink-500"
							aria-hidden
						/>
						<div className="sr-only">Success: </div>
					</div>

					<h2 className="font-600 text-primary-foreground mb-[8px] text-[20px] leading-none">Create Aila apps template</h2>
					<p className="max-w-text mb-[24px]">
						Creating an template will add this app to the App templates section, where other educators can duplicate and customise it. The app’s configuration and settings will be shared, but its
						dataset will not be included.
					</p>

					<h3 className="font-600 text-primary-foreground mb-[8px] text-[18px] leading-none">Select tags for app template</h3>
					<p className="max-w-text mb-[24px]">Tags help other educators find, filter, and reuse your template based on common teaching goals or use cases.</p>
					<div className="mb-[24px] flex flex-wrap gap-[8px]">
						{templateTags.map((tag) => (
							<Label
								key={tag}
								className="bg-light-grey-25 flex items-center gap-[8px] p-[8px]"
							>
								<Checkbox
									checked={selectedTemplateTags.includes(tag)}
									disabled={isCreatingTemplate || isTemplateCreated}
									onCheckedChange={(checked) => {
										if (checked) {
											setSelectedTemplateTags([...selectedTemplateTags, tag])
										} else {
											setSelectedTemplateTags(selectedTemplateTags.filter((t) => t !== tag))
										}
									}}
								/>
								<div className="text-navy-100 text-[14px]">{tag}</div>
							</Label>
						))}
					</div>

					<Button
						variant="outline"
						onClick={() => {
							createAssistantTemplate({ assistant: app, tags: [...selectedTemplateTags] })
							setSelectedTemplateTags([])
						}}
						disabled={isCreatingTemplate || isTemplateCreated || selectedTemplateTags.length === 0}
						className="min-w-[123px]"
					>
						{isCreatingTemplate ? (
							<>
								<Spinner />
								Creating...
							</>
						) : (
							'Create template'
						)}
					</Button>

					{isTemplateCreated && (
						<div
							className="max-w-text bg-light-green-25 text-navy-100 mt-[24px] mb-[24px] flex items-center gap-[10px] p-[12px]"
							role="alert"
						>
							<div className="bg-light-green-50 flex size-[32px] shrink-0 items-center justify-center">
								<MdCheckCircle
									className="size-[20px]"
									aria-hidden
								/>
								<div className="sr-only">Success: </div>
							</div>
							<div className=" ">
								Your app template has been created. You can now find it within the{' '}
								<Link
									from="/educator/courses/$courseId"
									to="/educator/courses/$courseId/apps"
									hash="templates"
									className="underline"
								>
									App templates
								</Link>{' '}
								section.
							</div>
						</div>
					)}

					<Separator className="my-[24px]" />

					<h2 className="font-600 text-primary-foreground mb-[8px] text-[20px] leading-none">Delete Aila app</h2>
					<p className="max-w-text mb-[24px]">Deleting this Aila app will remove it for all admins and students. This action cannot be undone, and the app cannot be recovered.</p>
					<DeleteButtonWithConfirmationDialog
						buttonLabel="Delete Aila app"
						title="Delete Aila app"
						onDelete={() => {
							deleteAssistant(appId)
							void navigate({ from: '/educator/courses/$courseId', to: './apps' })
						}}
					>
						Are you sure you want to delete this Aila app?
					</DeleteButtonWithConfirmationDialog>

					<Separator className="my-[24px]" />
				</>
			)}
		</>
	)
}
